import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * <pack-spec data-product-id="…">25g pack · 10 × 2.5g pieces</pack-spec>
 *
 * Keeps the variant-level pack spec in step with the selected variant. The spec
 * table (bw-spec-table) can render in several sections at once — the main
 * product-information section, a standalone section, product-detail-columns — and
 * the variant picker repeats the value under its Weight select, but only the
 * variant picker's OWN subtree is morphed on variant change. So we listen on
 * `document` (catching the event wherever it fires) and read the new value from
 * the re-rendered page the picker already fetched (event.detail.data.html),
 * matching this element to its counterpart there by product id and position.
 *
 * Why not an embedded variant→text map, as before: Liquid exposes at most 250 of
 * a product's variants and the tape products carry 630, so every 20"+ variant was
 * missing from the map and the whole Pack row was hidden the moment a shopper
 * picked one. The fetched page is rendered for the exact variant, so it has no
 * such cap.
 *
 * On any miss — a different product's picker, a section-only render without this
 * element, a variant with no pack spec — the current text is left alone. A stale
 * pack line is better than none.
 */
class PackSpec extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.variantUpdate, this.updatePackSpec);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.variantUpdate, this.updatePackSpec);
  }

  /**
   * Updates the pack spec value for the selected variant.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePackSpec = (event) => {
    const html = event.detail?.data?.html;
    const productId = event.detail?.data?.productId;
    const ownId = this.dataset.productId;
    if (!html || !ownId || String(productId) !== ownId) return;

    const selector = `pack-spec[data-product-id="${ownId}"]`;
    const mine = Array.from(document.querySelectorAll(selector));
    const theirs = html.querySelectorAll(selector);
    const counterpart = theirs[mine.indexOf(this)] ?? theirs[0];
    const value = counterpart?.textContent?.trim();
    if (!value) return;

    this.textContent = value;
    const row = this.closest('tr');
    if (row) row.hidden = false;
  };
}

if (!customElements.get('pack-spec')) {
  customElements.define('pack-spec', PackSpec);
}
